// Wire types for /api/units/search and /api/units/common. Mirror of the
// `unit_catalog::dto` structs.

export interface UnitSearchHit {
  uri: string;
  source: "qudt" | "custom";
  label: string | null;
  symbol: string | null;
  alpha_code: string | null;
  quantity_kind_uri: string | null;
  // Server-resolved via LEFT JOIN on quantity_kinds so the search results
  // view can render the same right-side chip as the common-units view.
  quantity_kind_label: string | null;
  deprecated: boolean;
  score: number;
}

export interface QkindSearchHit {
  uri: string;
  label: string;
  score: number;
}

export interface SearchResponse {
  units: UnitSearchHit[];
  qkinds: QkindSearchHit[];
}

/** Bare `(uri, label)` reference returned by `/api/units/qkind-context`. */
export interface QkindRef {
  uri: string;
  label: string;
}

/**
 * Hierarchy context for a pinned quantity kind. Drives the chip strip in
 * browse mode: ancestors render LEFT of the pinned chip, children render
 * RIGHT for drill-down.
 *
 * - `ancestors[0]` is the direct broader kind, last entry is the root.
 *   Does NOT include the pinned kind itself.
 * - `children` are direct sub-kinds whose subtree has at least one resolved
 *   unit (server-side EXISTS filter). Capped at 12.
 */
export interface QkindContext {
  ancestors: QkindRef[];
  children: QkindRef[];
}

export interface UnitDetails {
  uri: string;
  /** `"qudt"` for QUDT-vendored rows; `"custom"` for MyCorr extensions
   *  loaded from `custom-units.json` (e.g. `custom:GM-PER-TONNE`). */
  source: "qudt" | "custom";
  label: string | null;
  symbol: string | null;
  alpha_code: string | null;
  minor_units: number | null;
  quantity_kind_uri: string | null;
  quantity_kind_label: string | null;
  multiplier_to_si: number | null;
  offset_to_si: number | null;
  ucum_code: string | null;
  unece_code: string | null;
  dimension_vector: string | null;
  deprecated: boolean;
  /**
   * Prefixed QUDT URIs this unit is numerically identical to via
   * `qudt:exactMatch` (e.g. `unit:TONNE` → `["unit:TON_Metric"]`). Sorted;
   * max 3 per unit in QUDT v3.3.0. Empty when no equivalences exist.
   * Surfaced in `UnitInfoCard` as the "Equivalent to" section.
   */
  exact_matches: string[];
}

// What the WASM bridge accepts for set_column_unit_assignment, plus the
// server-resolved display fields the row badge reads. Inbound writes only
// populate the canonical fields (uri/source/scale_exponent); the server
// fills in `symbol`/`label`/`alpha_code` on the way back out, so the badge
// renders without a separate catalog lookup. All three are `null`/missing
// when the URI doesn't resolve.
//
// `scale_exponent` is a signed power-of-ten: `0` = ×1, `3` = K, `-2` = %.
// The display label (`K`/`M`/`%`/…) is derived via `deriveScaleLabel` —
// kept in sync with `common_core::column::column_state::derived_scale_label`.
export interface UnitAssignment {
  stored_unit_uri: string;
  stored_unit_source: "qudt" | "custom";
  scale_exponent: number;
  symbol?: string | null;
  label?: string | null;
  alpha_code?: string | null;
}

/** Inclusive bounds for `scale_exponent`. Mirrors the Rust constants. */
export const MIN_SCALE_EXPONENT = -12;
export const MAX_SCALE_EXPONENT = 12;

/**
 * Map a scale exponent to its short SI-style label, when one exists.
 * Returns `null` for `0` (identity) and for any value without a curated
 * short label — callers render those as `10^N`.
 *
 * Mirror of `common_core::column::column_state::derived_scale_label`. The
 * tests in `common_core/tests/test_unit_assignment_display_label.rs` pin
 * the canonical mapping; keep this in sync.
 */
export function deriveScaleLabel(exponent: number): string | null {
  switch (exponent) {
    case -6:
      return "µ";
    case -3:
      return "m";
    case -2:
      return "%";
    case 0:
      return null;
    case 3:
      return "K";
    case 6:
      return "M";
    case 9:
      return "B";
    default:
      return null;
  }
}

/** Multiplicative factor `10^exponent`. */
export function scaleFactor(exponent: number): number {
  return Math.pow(10, exponent);
}
